import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Badge,
  Tooltip,
  Avatar,
} from '@mui/material';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add, Edit, Delete, CheckCircle, Cancel, GroupAdd, Wifi, CreditCard } from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { ACCENTS, tint, colorForName } from '../theme/accents';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import { formatINR } from '../utils/currency';
import { useSSE } from '../hooks/useSSE';
import QuickCollectionMulti from '../components/QuickCollectionMulti';
import MemberDetailDialog from '../components/MemberDetailDialog';
import { StatusIndicator, MethodIndicator, MethodOptionContent, StatusOptionContent, METHOD_VALUES } from '../components/Indicators';

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<CreditCard />}
    accent={ACCENTS.emerald}
    title="No payments for this filter"
    description="Nothing matches the month and member selected. Clear a filter, or use Add Payment to record one."
  />
);

const Payments = () => {
  const { isAdmin } = useAuth();
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [multiDialogOpen, setMultiDialogOpen] = useState(false);
  const [realtimeUpdates, setRealtimeUpdates] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [filters, setFilters] = useState({
    month: dayjs().format('YYYY-MM'),
    userId: '',
    status: '',
  });
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    amount: 0,
    paymentMonth: dayjs().format('YYYY-MM'),
    paymentDate: dayjs(),
    paymentMethod: 'Cash',
    paidByUserId: null,
    status: 'completed',
    notes: '',
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`/api/payments?${params}`);
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // SSE event handler
  const handleSSEEvent = useCallback((event) => {
    if (event.type === 'payment_created') {
      // Increment counter to trigger visual feedback
      setRealtimeUpdates(prev => prev + 1);
      
      // Show toast notification
      toast.success(`Payment received: ${formatINR(event.data.amount)} from ${event.data.userName}`, {
        duration: 4000,
        icon: '💰'
      });
      
      // Refresh payments if it affects current view
      fetchPayments();
    }
  }, [fetchPayments]);

  // Initialize SSE connection
  const { isConnected } = useSSE(handleSSEEvent);

  // Base share x the member's own multiplier — same rule the backend charges.
  const contributionForMember = (member) => {
    const base = Number(settings?.monthlyAmount) || 0;
    const multiplier = Number(member?.contribution_multiplier) || 1;
    return Math.round(base * multiplier);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/rules/settings');
        setSettings(response.data);
      } catch (error) {
        // Amount stays manually entered if settings are unavailable
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, [filters]);

  // Listen for payment creation events from QuickCollection in Layout
  useEffect(() => {
    const handlePaymentCreated = (event) => {
      console.log('Payment created event received:', event.detail);
      // Refresh payments table when payment is created from QuickCollection
      fetchPayments();
      // Also increment the realtime update counter for visual feedback
      setRealtimeUpdates(prev => prev + 1);
    };

    window.addEventListener('paymentCreated', handlePaymentCreated);
    
    return () => {
      window.removeEventListener('paymentCreated', handlePaymentCreated);
    };
  }, [fetchPayments]);

  const fetchUsers = async () => {
    try {
      // The system admin is not a paying member of the group, so it must not be
      // offered anywhere member figures are recorded — see Members/Loans, which
      // already filter this way.
      const response = await axios.get('/api/users');
      setUsers(response.data.filter((user) => !user.is_system_admin));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleOpenDialog = (payment = null) => {
    if (payment) {
      setEditMode(true);
      setSelectedPayment(payment);
      setFormData({
        userId: payment.user_id,
        amount: payment.amount,
        paymentMonth: payment.payment_month,
        paymentDate: dayjs(payment.payment_date),
        paymentMethod: payment.payment_method || 'Cash',
        paidByUserId: payment.paid_by_user_id,
        status: payment.status,
        notes: payment.notes || '',
      });
    } else {
      setEditMode(false);
      setSelectedPayment(null);
      setFormData({
        userId: '',
        amount: 0,
        paymentMonth: dayjs().format('YYYY-MM'),
        paymentDate: dayjs(),
        paymentMethod: 'Cash',
        paidByUserId: null,
        status: 'completed',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedPayment(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        paymentDate: formData.paymentDate.format('YYYY-MM-DD'),
      };

      if (editMode) {
        await axios.put(`/api/payments/${selectedPayment.id}`, payload);
        toast.success('Payment updated successfully');
      } else {
        await axios.post('/api/payments', payload);
        toast.success('Payment created successfully');
      }
      
      handleCloseDialog();
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await axios.delete(`/api/payments/${id}`);
        toast.success('Payment deleted successfully');
        fetchPayments();
      } catch (error) {
        toast.error('Failed to delete payment');
      }
    }
  };

  // Totals for whatever the filters are currently showing
  const monthTotals = payments.reduce(
    (acc, payment) => {
      const amount = Number(payment.amount) || 0;
      if (payment.status === 'completed') {
        acc.collected += amount;
        acc.completedCount += 1;
      } else {
        acc.pending += amount;
      }
      return acc;
    },
    { collected: 0, pending: 0, completedCount: 0 }
  );

  const columns = [
    {
      field: 'user_name',
      headerName: 'Member',
      flex: 2,
      minWidth: 170,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: tint(colorForName(params.value || ''), 0.2),
              color: colorForName(params.value || ''),
              border: '1px solid',
              borderColor: tint(colorForName(params.value || ''), 0.5),
            }}
          >
            {(params.value || '?').charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap className="member-name" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {params.value}
            </Typography>
            {/* The note is how the meeting is tracked - worth showing, not hiding.
                Paying on someone's behalf is the exception, so it is called out
                here rather than given a column that reads "Self" every row. */}
            {(params.row.notes || params.row.paid_by_name) && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {params.row.notes}
                {params.row.paid_by_name && (
                  <Box component="span" sx={{ color: ACCENTS.marigold, fontWeight: 600 }}>
                    {params.row.notes ? ' · ' : ''}by {params.row.paid_by_name}
                  </Box>
                )}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.9,
      minWidth: 95,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" className="tabular" sx={{ fontWeight: 700 }}>
          {formatINR(params.value)}
        </Typography>
      ),
    },
    {
      field: 'payment_month',
      headerName: 'Month',
      flex: 0.8,
      minWidth: 85,
      // 2026-08 is a storage format, not something to read in a table
      renderCell: (params) => dayjs(`${params.value}-01`).format('MMM YYYY'),
    },
    {
      field: 'payment_date',
      headerName: 'Paid on',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => dayjs(params.value).format('D MMM YYYY'),
    },
    {
      field: 'payment_method',
      headerName: 'Method',
      width: 130,
      sortable: false,
      renderCell: (params) => <MethodIndicator method={params.value} size="small" />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => <StatusIndicator status={params.value} size="small" />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 72,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          {isAdmin && (
            <>
              <Tooltip title="Edit payment">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(params.row); }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete payment">
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(params.row.id); }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Money in"
        accent={ACCENTS.emerald}
        title="Payments"
        subtitle="Every monthly contribution, who paid it and how."
        actions={isAdmin && (
          <>
            <Button variant="outlined" startIcon={<GroupAdd />} onClick={() => setMultiDialogOpen(true)}>
              Multi Collection
            </Button>
            {/* The theme already styles containedPrimary; the gradient that used
                to be inlined here was frozen at the dark palette. */}
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Payment
            </Button>
          </>
        )}
      />

      {/* Live-connection state sits with the table it affects, not in the title */}
      {realtimeUpdates > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {realtimeUpdates} live {realtimeUpdates === 1 ? 'update' : 'updates'} received
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Filter by Month"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Months</MenuItem>
              {[...Array(12)].map((_, i) => {
                const month = dayjs().subtract(i, 'month').format('YYYY-MM');
                return (
                  <MenuItem key={month} value={month}>
                    {dayjs(month).format('MMMM YYYY')}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Filter by Member"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Members</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              size="small"
            >
              <MenuItem value="">All statuses</MenuItem>
              {['completed', 'pending', 'overdue'].map(v => (
                <MenuItem key={v} value={v}><StatusOptionContent status={v} /></MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* What the filtered month adds up to - the question you always ask of this page */}
      {payments.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: tint(ACCENTS.emerald, 0.3),
            background: `linear-gradient(120deg, ${tint(ACCENTS.emerald, 0.12)} 0%, rgba(0,0,0,0) 60%)`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Collected</Typography>
              <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.emerald }}>
                {formatINR(monthTotals.collected)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Still pending</Typography>
              <Typography
                className="tabular"
                variant="h6"
                sx={{ color: monthTotals.pending > 0 ? ACCENTS.vermilion : ACCENTS.emerald }}
              >
                {formatINR(monthTotals.pending)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Records</Typography>
              <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.indigo }}>
                {monthTotals.completedCount}/{payments.length}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Showing</Typography>
              <Typography variant="h6" sx={{ color: ACCENTS.marigold }}>
                {filters.month ? dayjs(`${filters.month}-01`).format('MMM YYYY') : 'All months'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ width: '100%', border: '1px solid', borderColor: 'divider' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'user_name', amount: 'amount' }}
          rows={payments}
          columns={columns}
          autoHeight
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => setDetailMemberId(params.row.user_id)}
          disableColumnMenu
          loading={loading}
          rowHeight={56}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'divider',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: tint(ACCENTS.emerald, 0.07),
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Payment' : 'Add New Payment'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Member"
                value={formData.userId}
                onChange={(e) => {
                  const member = users.find((u) => u.id === e.target.value);
                  setFormData({
                    ...formData,
                    userId: e.target.value,
                    // Members are on different share counts, so a flat default was
                    // wrong for everyone; follow the selected member instead.
                    amount: contributionForMember(member),
                  });
                }}
                required
                disabled={editMode}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Month"
                value={formData.paymentMonth}
                onChange={(e) => setFormData({ ...formData, paymentMonth: e.target.value })}
                required
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Payment Date"
                value={formData.paymentDate}
                onChange={(newValue) => setFormData({ ...formData, paymentDate: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Payment Method"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              >
                {METHOD_VALUES.map(m => (
                  <MenuItem key={m} value={m}><MethodOptionContent method={m} /></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Paid By (if on behalf)"
                value={formData.paidByUserId || ''}
                onChange={(e) => setFormData({ ...formData, paidByUserId: e.target.value || null })}
              >
                <MenuItem value="">Self</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                {['completed', 'pending', 'overdue'].map(v => (
                  <MenuItem key={v} value={v}><StatusOptionContent status={v} /></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Collection Dialog */}
      <QuickCollectionMulti
        open={multiDialogOpen}
        onClose={() => setMultiDialogOpen(false)}
        onSuccess={() => {
          fetchPayments();
          setRealtimeUpdates(prev => prev + 1);
        }}
      />

      <MemberDetailDialog
        memberId={detailMemberId}
        open={Boolean(detailMemberId)}
        onClose={() => setDetailMemberId(null)}
      />
    </Box>
  );
};

export default Payments;